/**
 * MODULE: _shared/frontend-shell/src/lib/reloadOnStaleChunk.ts
 * PURPOSE: One-shot full reload when a deploy leaves the tab on stale hashed chunks
 * DEPENDENCIES: none (browser sessionStorage + Vite preloadError)
 * PUBLIC_API: installReloadOnStaleChunk
 * SIDE_EFFECTS: may call location.reload once per session key
 * AGENT SAFETY: 🟢 SAFE
 * FILE_SIZE: ~45 / 150
 * RECENT: 2026-08-06 [2.2.2130] — ACA sprint close-out; still recovers post-deploy hashed asset 404s
 * RECENT_PRIOR: 2026-08-05 [2.2.2128] — recover from post-deploy dynamic import 404s
 * See: _shared/frontend-shell/nginx.aca.conf, CHANGELOG.md
 */

const RELOAD_KEY = 'brazn:stale-chunk-reload';

function isStaleChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

/** Reload once when Vite/React hits a missing hashed asset after a release. */
export function installReloadOnStaleChunk(): void {
  if (typeof window === 'undefined') return;

  const tryReload = (reason: string) => {
    try {
      if (sessionStorage.getItem(RELOAD_KEY) === '1') return;
      sessionStorage.setItem(RELOAD_KEY, '1');
    } catch {
      // private mode — still attempt one reload
    }
    console.warn(`[reloadOnStaleChunk] ${reason}; reloading once for fresh index.html`);
    window.location.reload();
  };

  window.addEventListener('vite:preloadError', ((event: Event) => {
    event.preventDefault();
    tryReload('vite:preloadError');
  }) as EventListener);

  window.addEventListener('unhandledrejection', (event) => {
    if (isStaleChunkError(event.reason)) {
      event.preventDefault();
      tryReload('unhandledrejection');
    }
  });
}

/** True when ErrorBoundary should offer hard reload for stale deploy assets. */
export function isStaleChunkMessage(message: string): boolean {
  return isStaleChunkError(new Error(message));
}
